/** Local-only card preview. Native multipart submission remains the browser's responsibility. */
(() => {
  if (customElements.get('photo-hello-cards')) return;
  class PhotoHelloCards extends HTMLElement {
    #messages = {};
    #t(key, fallback) { return this.#messages[key] || fallback; }
    #abort;
    #objectURL = null;
    #epoch = 0;
    connectedCallback() {
      try { this.#messages = JSON.parse(this.querySelector('[data-b2h-form-i18n]')?.textContent || '{}'); } catch { this.#messages = {}; }
      this.#abort?.abort();
      this.#abort = new AbortController();
      const options = { signal: this.#abort.signal };
      this.querySelector('[data-card-photo]')?.addEventListener('change', () => this.#photo(), options);
      for (const key of ['name', 'hello']) this.querySelector(`[data-card-${key}]`)?.addEventListener('input', () => this.#text(), options);
      this.#text();
      this.#photo();
    }
    disconnectedCallback() {
      this.#abort?.abort();
      this.#epoch += 1;
      this.#release();
    }
    /** Editable own-layout SVG export for an explicit fulfillment call; no automatic upload/download. */
    async exportSVG() {
      const file = this.querySelector('[data-card-photo]')?.files?.[0];
      const name = this.querySelector('[data-card-name]');
      const hello = this.querySelector('[data-card-hello]');
      const source = this.querySelector('.b2h-hello__card');
      if (!file || !name?.checkValidity() || !hello?.checkValidity() || !source || !this.#objectURL) throw new Error(this.#t('complete_first', "Complete the photo and name first."));
      const dataURL = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error(this.#t('read_failed', "Photo could not be read.")));
        reader.readAsDataURL(file);
      });
      this.#text();
      const copy = source.cloneNode(true);
      copy.setAttribute('width', '90mm');
      copy.setAttribute('height', '54mm');
      copy.setAttribute('data-layout-version', 'photo-hello-front-v1');
      const originals = [source, ...source.querySelectorAll('*')];
      const copies = [copy, ...copy.querySelectorAll('*')];
      for (const [index, node] of originals.entries()) {
        const style = getComputedStyle(node);
        for (const property of ['fill', 'font-family', 'font-size', 'font-weight', 'display']) copies[index].style.setProperty(property, style.getPropertyValue(property));
      }
      copy.querySelector('[data-card-image]').setAttribute('href', String(dataURL));
      return new XMLSerializer().serializeToString(copy);
    }
    #release() {
      if (this.#objectURL) URL.revokeObjectURL(this.#objectURL);
      this.#objectURL = null;
    }
    #photo() {
      const field = this.querySelector('[data-card-photo]');
      const image = this.querySelector('[data-card-image]');
      const placeholder = this.querySelector('[data-card-placeholder]');
      const error = this.querySelector('[data-card-error]');
      if (!field || !image || !placeholder || !error) return;
      const epoch = ++this.#epoch;
      this.#release();
      image.removeAttribute('href');
      placeholder.hidden = false;
      placeholder.style.display = '';
      field.setCustomValidity('');
      field.removeAttribute('aria-invalid');
      error.hidden = true;
      error.textContent = '';
      const file = field.files?.[0];
      if (!file) { this.#text(); return; }
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        this.#error(this.#t('photo_format', "Choose a JPG, PNG or WebP photo."));
        return;
      }
      this.#objectURL = URL.createObjectURL(file);
      const localURL = this.#objectURL;
      const probe = new Image();
      probe.onload = () => {
        if (!this.isConnected || epoch !== this.#epoch) return;
        image.setAttribute('href', localURL);
        placeholder.style.display = 'none';
        this.#text();
      };
      probe.onerror = () => {
        if (epoch !== this.#epoch) return;
        this.#release();
        this.#error(this.#t('photo_open_failed', "This photo could not be opened. Choose another JPG, PNG or WebP."));
      };
      probe.src = localURL;
    }
    #error(message) {
      const field = this.querySelector('[data-card-photo]');
      const error = this.querySelector('[data-card-error]');
      field?.setCustomValidity(message);
      field?.setAttribute('aria-invalid', 'true');
      if (error) { error.textContent = message; error.hidden = false; }
      const status = this.querySelector('[data-card-status]');
      if (status) status.textContent = message;
    }
    #text() {
      const enteredName = this.querySelector('[data-card-name]')?.value.trim() || '';
      const name = enteredName || this.#t('pet_name', "Pet name");
      const hello = this.querySelector('[data-card-hello]')?.value.trim() || '';
      this.#fit(this.querySelector('[data-card-name-preview]'), name, 46, 24, 130, 240, 220);
      this.#fit(this.querySelector('[data-card-hello-preview]'), hello, 24, 18, 250, 450, 280);
      const status = this.querySelector('[data-card-status]');
      if (status) status.textContent = this.#objectURL && enteredName ? this.#t('layout_ready', "Your card layout is ready to view.") : this.#t('layout_waiting', "Choose a photo and add a name to see the layout.");
    }
    /** Fit all supplied characters within the right-hand wireframe area, without truncation. */
    #fit(node, value, maximum, minimum, top, bottom, singleBaseline) {
      if (!node) return;
      const segments = typeof Intl.Segmenter === 'function' ? [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(value)].map(item => item.segment) : Array.from(value);
      let lines = [], size = maximum;
      for (; size >= minimum; size -= 1) {
        node.style.fontSize = `${size}px`;
        lines = [];
        let line = '';
        for (const character of segments) {
          node.textContent = line + character;
          if (node.getComputedTextLength() > 310 && line) {
            lines.push(line.trimEnd());
            line = character.trimStart();
          } else line += character;
        }
        if (line) lines.push(line.trimEnd());
        if (lines.length * size * 1.2 <= bottom - top) break;
      }
      node.textContent = '';
      node.style.fontSize = `${Math.max(size, minimum)}px`;
      const lineHeight = Math.max(size, minimum) * 1.2;
      for (const [index, line] of lines.entries()) {
        const span = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
        span.setAttribute('x', '560');
        span.setAttribute('y', String(lines.length === 1 ? singleBaseline : top + Math.max(size, minimum) + index * lineHeight));
        span.textContent = line;
        node.append(span);
      }
    }
  }
  customElements.define('photo-hello-cards', PhotoHelloCards);
})();
